import { reactive, watch, onScopeDispose } from "vue";
import { getClientId } from "@/utils/clientId";

/**
 * Aviso de cadastro criado/alterado fora das telas — hoje, pelo card do
 * assistente, que chama o service direto (`chatActions.ts`) sem passar pelos
 * stores. Com o assistente em meia tela o usuário fica NA tela da lista, e
 * clicar no menu da própria rota não remonta a página: sem este aviso, o
 * cadastro novo só aparecia entrando por outra tela (14/09/2026).
 *
 * O aviso do servidor (FEAT-044) entra pelo mesmo caminho: o backend emite
 * `entidade.alterada` na sala do terreiro a cada escrita de cadastro, e
 * `notifyServerEntityEvent` traduz isso no mesmo `notifyEntityChanged` — assim
 * a tela não precisa saber se a alteração veio de outra aba ou de outro
 * dirigente da casa.
 */
const revisions = reactive<Record<string, number>>({});

/** Evento `entidade.alterada` do servidor, como o socket entrega. */
export interface ServerEntityEvent {
	resource?: string;
	origin?: string;
}

/** Chave que TODO assinante observa, para recarregar tudo de uma vez. */
const ALL = "*";

/**
 * Manda todas as telas abertas recarregarem. Serve para o retorno do socket: o
 * que mudou enquanto ele estava fora não gera evento nenhum, e sem isto a tela
 * seguiria com a lista do momento da queda.
 */
export function notifyAllEntitiesChanged(): void {
	revisions[ALL] = (revisions[ALL] ?? 0) + 1;
}

/**
 * Traduz o aviso do servidor em recarga local, ignorando o eco da própria
 * página (ela já recarregou quando gravou). Exportado avulso porque quem escuta
 * o socket é o layout, uma vez, e não cada tela.
 */
export function notifyServerEntityEvent(event: ServerEntityEvent): void {
	if (!event?.resource) return;
	if (event.origin && event.origin === getClientId()) return;
	revisions[event.resource] = (revisions[event.resource] ?? 0) + 1;
}

export function useEntityRefresh() {
	return {
		/** Um cadastro daquele tipo mudou: quem estiver ouvindo recarrega. */
		notifyEntityChanged: (resource: string) => {
			revisions[resource] = (revisions[resource] ?? 0) + 1;
		},
		/**
		 * Recarrega a tela quando um dos cadastros mudar. Chame no setup do
		 * componente: o watch morre junto com ele.
		 *
		 * `deferWhile`: enquanto devolver `true` (formulário de edição aberto),
		 * o aviso fica guardado e a recarga só roda quando ele fechar. Sem isso a
		 * recarga troca o objeto que o formulário recebe, e o formulário — que
		 * observa a prop — joga fora o que o usuário estava digitando. Basta outra
		 * aba mexer em QUALQUER cadastro do mesmo tipo (achado do Codex).
		 */
		onEntityChanged: (
			resources: string[],
			reload: () => unknown,
			options: { deferWhile?: () => boolean } = {},
		) => {
			// Uma recarga por vez: dois avisos juntos (o card cria dois cadastros
			// seguidos) fariam duas leituras correndo, e a mais antiga poderia
			// chegar por último e mostrar a lista sem o registro novo.
			//
			// Os avisos que chegam DURANTE uma recarga viram um só no fim, em vez de
			// enfileirar um por aviso: com o evento do servidor uma importação em
			// lote vira rajada, e a fila antiga fazia uma leitura por evento — todas
			// elas depois da primeira, e todas mostrando o mesmo resultado.
			let running = false;
			let pending = false;
			let disposed = false;
			let deferred = false;

			// Saiu da tela: o que ainda estava por recarregar não recarrega. Sem
			// isto a fila continuava gravando nos stores depois de navegar.
			onScopeDispose(() => {
				disposed = true;
			});

			const runReload = async () => {
				if (disposed) return;
				if (options.deferWhile?.()) {
					deferred = true;
					return;
				}
				if (running) {
					pending = true;
					return;
				}
				running = true;
				try {
					await reload();
				} catch {
					// Erro de recarga é problema de quem recarrega (a tela mostra o
					// aviso dela); aqui só não pode travar a próxima.
				} finally {
					running = false;
					if (pending && !disposed) {
						pending = false;
						void runReload();
					}
				}
			};

			watch(
				() =>
					[...resources, ALL]
						.map((resource) => revisions[resource] ?? 0)
						.join("|"),
				() => {
					void runReload();
				},
			);

			// Formulário fechou com aviso guardado: agora sim, recarrega.
			if (options.deferWhile) {
				watch(options.deferWhile, (blocked) => {
					if (blocked || !deferred) return;
					deferred = false;
					void runReload();
				});
			}
		},
	};
}
